import * as React from "/vendor/react";
import gql from "/vendor/graphql-tag";

import { ApolloError } from "/vendor/apollo-client";
import { isUnreachable } from "/lib/util/fetchError";
import { PollingDuration } from "/lib/constant";

import {
  parseIntParam,
  parseStringParam,
  parseArrayParam,
} from "/lib/util/params";
import {
  useApolloClient,
  useBreakpoint,
  useConfigurationProvider,
  useFilterParams,
  useSearchParams,
  useQuery,
  UseQueryResult,
  useRouter,
} from "/lib/component/util";
import { MobileFullWidthContent, Content, Loader } from "/lib/component/base";
import {
  AppLayout,
  BoundFilterList,
  EntitiesList,
  EntitiesListToolbar,
  NotFound,
} from "/lib/component/partial";

import createSilence from "/lib/mutation/createSilence";
import deleteSilence from "/lib/mutation/deleteSilence";
import deleteEntity from "/lib/mutation/deleteEntity";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { Pie, PieChart, Cell, ResponsiveContainer } from "recharts";
import {
  eventsViewFragments,
  useEventsDashViewQueryVariables,
} from "/lib/component/view/EventsView";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import axios from "axios";
import NamespaceIcon from "../partial/NamespaceIcon/Icon";
import { last } from "lodash";
import { exportEntities } from "/lib/util/exportAPI";

const useStyles = makeStyles({
  tableCell: {
    color: "white",
  },
});

export const entitiesViewFragments = {
  namespace: gql`
    fragment EntitiesView_namespace on Namespace {
      id
      ...EntitiesList_namespace
    }

    ${EntitiesList.fragments.namespace}
  `,
};

export const entitiesViewQuery = gql`
  query EntitiesViewQuery(
    $namespace: String!
    $limit: Int
    $offset: Int
    $order: EntityListOrder
    $filters: [String!]
  ) {
    namespace(name: $namespace) {
      id
      ...EntitiesView_namespace
    }
  }

  ${entitiesViewFragments.namespace}
`;

const entitiesDashViewQuery = gql`
  query EntitiesViewQuery(
    $namespace: String!
    $limit: Int
    $offset: Int
    $order: EntityListOrder
    $filters: [String!]
  ) {
    namespace(name: $namespace) {
      id
      entities(
        limit: $limit
        offset: $offset
        filters: $filters
        orderBy: $order
      ) @connection(key: "entities", filter: ["filters", "orderBy"]) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

const eventsViewQuery = gql`
  query EventsViewQuery(
    $namespace: String!
    $filters: [String!]
    $order: EventsListOrder
    $limit: Int
    $offset: Int
  ) {
    namespace(name: $namespace) {
      id
      ...EventsView_namespace
    }
  }

  ${eventsViewFragments.namespace}
`;

interface Variables {
  namespace: string;
  limit: number;
  offset: number;
  order: string;
  filters: string[];
}

export const useEntitiesViewQueryVariables = (): Variables => {
  const [params] = useSearchParams();
  const config = useConfigurationProvider();

  const limit = parseIntParam(params.limit, config.preferences.pageSize);
  const offset = parseIntParam(params.offset, 0);
  const order = parseStringParam(params.order, "ID");
  const filters = parseArrayParam(params.filters);

  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );

  return {
    namespace,
    limit,
    offset,
    order,
    filters,
  };
};

export const useEntitiesDashViewQueryVariables = (): Variables => {
  const [params] = useSearchParams();
  const config = useConfigurationProvider();

  const limit = parseIntParam(params.limit, config.preferences.pageSize);
  const offset = parseIntParam(params.offset, 0);
  const order = parseStringParam(params.order, "ID");
  const filters = ["class:agent"];

  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );

  return {
    namespace,
    limit,
    offset,
    order,
    filters,
  };
};

interface EntitiesViewContentProps {
  toolbarContent?: React.ReactNode;
  toolbarItems?: React.ReactNode;
  query: UseQueryResult<any, any>;
  variables: Variables;
  onCreateSilence: (_: any) => void;
  onDeleteSilence: (_: any) => void;
  onDelete: (_: any) => Promise<any>;
}

interface EntitiesViewDashContentProps extends EntitiesViewContentProps {
  eventsQuery: UseQueryResult<any, any>;
}

export const EntitiesDashViewContent = ({
  query,
  eventsQuery,
}: EntitiesViewDashContentProps) => {
  const { data = {}, networkStatus, aborted } = query;

  const {
    data: eventsData = {},
    networkStatus: eventsNetworkStatus,
    aborted: eventsAborted,
  } = eventsQuery;

  const loading = networkStatus < 6 || eventsNetworkStatus < 6;

  const classes = useStyles();
  const theme = useTheme();

  if (
    !data.namespace &&
    !eventsData.namespace &&
    !loading &&
    !aborted &&
    !eventsAborted
  ) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return (
      <Content>
        <Loader loading={loading} />
      </Content>
    );
  }

  function getTotalEntities() {
    return data.namespace.entities.pageInfo.totalCount || 0;
  }

  function getTotalAgents() {
    // const agents = data.namespace.entities.nodes.map((a: any) => {
    //   return a.entityClass
    // })
    // return agents.filter((a: any) => a === "agent").length || 0
    return data.namespace.entities.pageInfo.totalCount || 0;
  }

  // function getProxyEntities() {
  //   const agents = data.namespace.entities.nodes.map((a: any) => {
  //     return a.entityClass
  //   })
  //   return agents.filter((a: any) => a === "proxy").length || 0
  // }
  //
  // function getOtherEntities() {
  //   const agents = data.namespace.entities.nodes.map((a: any) => {
  //     return a.entityClass
  //   })
  //   return agents.filter((a: any) => a !== "proxy" && a !== "agent").length || 0
  // }

  function getTotalKeepalives() {
    return data.namespace.entities.pageInfo.totalCount || 0;
  }

  function getUnresponsiveKeepalives() {
    const agents = eventsData.namespace.events.nodes.map((a: any) => {
      return a.check;
    });
    return (
      agents.filter((a: any) => a.name === "keepalive" && a.state !== "passing")
        .length || 0
    );
  }

  return (
    <div>
      <Content>
        <TableContainer
          component={Paper}
          className={classes.table}
          style={{ backgroundColor: theme.palette.background.paper }}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>
                  Terminal Details
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>
                  Terminals
                </TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {getTotalEntities()}
                </TableCell>
              </TableRow>
              {/* <TableRow>
                  <TableCell className={theme.palette.text.primary}>
                    Agents
                  </TableCell>
                  <TableCell className={theme.palette.text.primary}>
                    {getTotalAgents()}
                  </TableCell>
                </TableRow> */}
              {/* <TableRow>
                  <TableCell className={theme.palette.text.primary}>
                    Proxy Entities
                  </TableCell>
                  <TableCell className={theme.palette.text.primary}>
                    0
                  </TableCell>
                </TableRow> */}
              {/* <TableRow>
                  <TableCell className={theme.palette.text.primary}>
                    Other Entities
                  </TableCell>
                  <TableCell className={theme.palette.text.primary}>
                    0
                  </TableCell>
                </TableRow> */}
              <TableRow>
                <TableCell className={theme.palette.text.primary}>
                  Keepalives
                </TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {getTotalKeepalives() - getUnresponsiveKeepalives()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>
                  Unresponsive
                </TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {getUnresponsiveKeepalives()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Content>
    </div>
  );
};

export const EntitiesPieChartDashContent = ({
  query,
}: EntitiesViewContentProps) => {
  const { data = {}, networkStatus, aborted } = query;

  const loading = networkStatus < 6;

  if (!data.namespace && !loading && !aborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return (
      <Content>
        <Loader loading={loading} />
      </Content>
    );
  }

  function getTotalAgents() {
    // const agents = data.namespace.entities.nodes.map((a: any) => {
    //   return a.entityClass
    // })
    // return agents.filter((a: any) => a === "agent").length || 0
    return data.namespace.entities.pageInfo.totalCount || 0;
  }

  function getOtherEntities() {
    // const agents = data.namespace.entities.nodes.map((a: any) => {
    //   return a.entityClass
    // })
    // return agents.filter((a: any) => a !== "agent").length || 0
    return 0;
  }

  const pieData: any = [
    {
      name: "Other",
      value: getOtherEntities(),
    },
    {
      name: "Agents",
      value: getTotalAgents(),
    },
  ];

  const COLORS = ["#0088FE", "#00C49F"];
  const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.3;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (pieData[index].value === 0) {
      return;
    }

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${pieData[index].name} - ${pieData[index].value}`}
      </text>
    );
  };

  return (
    <Content>
      <Paper
        style={{
          backgroundColor: "#424242",
          height: "auto",
          width: "100%",
        }}
      >
        <ResponsiveContainer aspect={1}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              label={renderCustomizedLabel}
              labelLine={false}
              isAnimationActive={false}
              outerRadius={130}
              fill="#8884d8"
            >
              {pieData.map((entry: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{ textAlign: "center", fontFamily: "Arial", color: "white" }}
        >
          TERMINAL CLASSES
        </div>
      </Paper>
    </Content>
  );
};

export const EntitiesViewContent = ({
  query,
  toolbarContent,
  toolbarItems,
  variables,
  ...props
}: EntitiesViewContentProps) => {
  const [, setFilters] = useFilterParams();
  const [, setParams] = useSearchParams();
  const isSmViewport = !useBreakpoint("sm", "gt");
  const router = useRouter();
  const { data = {}, networkStatus, aborted, refetch } = query;

  // see: https://github.com/apollographql/apollo-client/blob/master/packages/apollo-client/src/core/networkStatus.ts
  const loading = networkStatus < 6;

  if (!data.namespace && !loading && !aborted) {
    return (
      <AppLayout>
        <NotFound />
      </AppLayout>
    );
  }

  function redirectEntity(event: any) {
    router.history.push(`entities/${event}`);
  }

  return (
    <AppLayout>
      <div>
        <Content marginBottom>
          <EntitiesListToolbar
            onClickReset={() =>
              setParams((params) => ({
                ...params,
                filters: undefined,
                order: undefined,
              }))
            }
            onClickExport={()=>{
              
              const namespace = data.namespace.name
              if (namespace === "catskill"){
                alert("Catskill is currently not supported! ")
                return 
              }
              exportEntities(namespace)
            }}
            toolbarContent={toolbarContent}
            toolbarItems={toolbarItems}
          />
        </Content>

        <MobileFullWidthContent>
          <EntitiesList
            editable={!isSmViewport}
            limit={variables.limit}
            offset={variables.offset}
            loading={(loading && !data.namespace) || aborted}
            filters={variables.filters}
            onChangeFilters={setFilters}
            onChangeQuery={setParams}
            namespace={data.namespace}
            refetch={refetch}
            redirectEntity={redirectEntity}
            order={variables.order}
            {...props}
          />
        </MobileFullWidthContent>
      </div>
    </AppLayout>
  );
};

EntitiesViewContent.defaultProps = {
  toolbarContent: <BoundFilterList />,
};

interface DashViewProps {
  type: "table" | "pieChart";
}

export const EntitiesDashView = ({ type }: DashViewProps) => {
  const client = useApolloClient();
  const onCreateSilence = React.useCallback(
    (vars) => createSilence(client, vars),
    [client],
  );
  const onDeleteSilence = React.useCallback(
    (vars) => deleteSilence(client, vars),
    [client],
  );
  const onDelete = React.useCallback((vars) => deleteEntity(client, vars), [
    client,
  ]);

  const variables = useEntitiesDashViewQueryVariables();
  const query = useQuery({
    query: entitiesDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.short,
    variables,
    onError: (error: Error): void => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const eventsVariables = useEventsDashViewQueryVariables("Incident");
  const eventsQuery = useQuery({
    query: eventsViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.short,
    variables: eventsVariables,
    onError: (error: Error): void => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  if (type === "table") {
    return (
      <EntitiesDashViewContent
        query={query}
        eventsQuery={eventsQuery}
        variables={variables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onDelete={onDelete}
      />
    );
  }

  if (type === "pieChart") {
    return (
      <EntitiesPieChartDashContent
        query={query}
        variables={variables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onDelete={onDelete}
      />
    );
  }

  return (
    <EntitiesDashViewContent
      query={query}
      eventsQuery={eventsQuery}
      variables={variables}
      onCreateSilence={onCreateSilence}
      onDeleteSilence={onDeleteSilence}
      onDelete={onDelete}
    />
  );
};

export const EntitiesView = () => {
  const client = useApolloClient();
  const onCreateSilence = React.useCallback(
    (vars) => createSilence(client, vars),
    [client],
  );
  const onDeleteSilence = React.useCallback(
    (vars) => deleteSilence(client, vars),
    [client],
  );
  const onDelete = React.useCallback((vars) => deleteEntity(client, vars), [
    client,
  ]);

  const variables = useEntitiesViewQueryVariables();
  const query = useQuery({
    query: entitiesViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.short,
    variables,
    onError: (error: Error): void => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  return (
    <EntitiesViewContent
      query={query}
      variables={variables}
      onCreateSilence={onCreateSilence}
      onDeleteSilence={onDeleteSilence}
      onDelete={onDelete}
    />
  );
};
